import { Link, useLocation } from 'react-router-dom';
import { Button } from "./ui/button";
import { Search, MapPin, CreditCard, Phone, Car, Train, Smartphone, Palette, Calendar, Trophy, ExternalLink } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip";

export function Navigation() {
  const location = useLocation();
  
  const pages = [
    { id: "/", title: "Home", icon: Search, path: "/" },
    { id: "/pin-codes", title: "PIN Codes", icon: MapPin, path: "/pin-codes" },
    { id: "/ifsc-codes", title: "IFSC Codes", icon: CreditCard, path: "/ifsc-codes" },
    { id: "/std-codes", title: "STD Codes", icon: Phone, path: "/std-codes" },
    { id: "/rto-codes", title: "RTO Codes", icon: Car, path: "/rto-codes" },
    { id: "/railway-codes", title: "Railway Codes", icon: Train, path: "/railway-codes" },
    { id: "/ussd-codes", title: "USSD Codes", icon: Smartphone, path: "/ussd-codes" },
    { id: "/html-colors", title: "HTML Colors", icon: Palette, path: "/html-colors" },
    { id: "/festivals", title: "Festival Calendar", icon: Calendar, path: "/festivals" },
    { id: "/cricket", title: "Cricket Winners", icon: Trophy, path: "/cricket" },
  ];

  // Show only Home and IFSC Codes on mobile
  const mobilePages = pages.filter(p => p.id === "/" || p.id === "/ifsc-codes");

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <Link to="/" className="text-xl font-semibold text-gray-900 hover:text-gray-700 whitespace-nowrap">
              Indian Codes Hub
            </Link>
          </div>
          <div className="hidden md:flex items-center gap-3 min-w-0">
            <div className="ml-6 flex items-baseline space-x-4 overflow-x-auto whitespace-nowrap">
              {pages.slice(0, 6).map((page) => {
                const Icon = page.icon;
                return (
                  <Link key={page.id} to={page.path}>
                    <Button
                      variant={location.pathname === page.path ? "default" : "ghost"}
                      size="sm"
                      className="flex items-center space-x-2"
                    >
                      <Icon className="h-4 w-4" />
                      <span>{page.title}</span>
                    </Button>
                  </Link>
                );
              })}
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button asChild variant="link" size="sm" className="ml-2 shrink-0 group underline underline-offset-4 text-indigo-700 hover:text-indigo-800">
                  <a
                    href="https://creatorsai.techkarmic.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Discover Guidance for Your Journey (opens in a new tab)"
                    className="inline-flex items-center"
                  >
                    Discover Guidance for Your Journey
                    <ExternalLink className="h-4 w-4 ml-1 transition-transform duration-200 group-hover:-translate-y-0.5" />
                  </a>
                </Button>
              </TooltipTrigger>
              <TooltipContent sideOffset={6}>Opens in new tab</TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation (minimal) */}
      <div className="md:hidden">
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-50">
          <div className="grid grid-cols-2 gap-2">
            {mobilePages.map((page) => {
              const Icon = page.icon;
              return (
                <Link key={page.id} to={page.path}>
                  <Button
                    variant={location.pathname === page.path ? "default" : "outline"}
                    size="sm"
                    className="flex items-center justify-center space-x-2 h-12 w-full"
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-xs">{page.title}</span>
                  </Button>
                </Link>
              );
            })}
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                asChild
                variant="link"
                size="sm"
                className="w-full h-12 mt-3 underline underline-offset-4 text-indigo-700 hover:text-indigo-800"
              >
                <a
                  href="https://creatorsai.techkarmic.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Discover Guidance for Your Journey (opens in a new tab)"
                  className="flex items-center justify-center"
                >
                  Discover Guidance for Your Journey
                  <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              </Button>
            </TooltipTrigger>
            <TooltipContent sideOffset={6}>Opens in new tab</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </nav>
  );
}